import { useLingui } from "@lingui/react/macro";
import type { Bot } from "@rakazo/contracts";
import { Button, Popover, PopoverContent, PopoverTrigger } from "@rakazo/ui-web";
import { Home, PanelLeftClose, PanelLeftOpen, Users } from "lucide-react";
import { useState } from "react";
import { IconTooltip } from "../../components/IconTooltip";
import { WorkspaceSwitcherButton } from "../../components/WorkspaceSession";
import { desktopBridge } from "../../lib/desktop";
import type { Panel } from "../../lib/right-panel-state";
import { WindowChrome } from "../WindowChrome";
import { BotCreatePicker } from "./bot-picker";

export function SidebarHeader({
  mini = false,
  canExpand = true,
  setBotsSidebarCollapsedPref,
  bots,
  setMobileSidebarOpen,
  setPanel,
  navigate,
  setNewSpaceOpen,
  setPickerInfoTopic,
}: {
  mini?: boolean;
  canExpand?: boolean;
  setBotsSidebarCollapsedPref: (collapsed: boolean) => void;
  bots: Bot[];
  setMobileSidebarOpen: (open: boolean) => void;
  setPanel: (panel: Panel) => void;
  navigate: (path: string) => void;
  setNewSpaceOpen: (open: boolean) => void;
  setPickerInfoTopic: (topic: "group" | "space") => void;
}) {
  const { t } = useLingui();
  const [createMenuOpen, setCreateMenuOpen] = useState(false);
  const hasWindowChrome = Boolean(desktopBridge());
  const toggleLabel = mini ? t`Show bots` : t`Minimize bots`;
  return (
    <div
      className={`app-drag flex items-center ${mini ? `flex-col gap-2 px-0 pb-2 ${hasWindowChrome ? "pt-4" : "pt-2"}` : "flex-wrap justify-between gap-y-2 px-3 pb-3 pt-4"}`}
    >
      {!mini || hasWindowChrome ? <WindowChrome /> : null}
      <div className={`relative flex items-center ${mini ? "flex-col gap-0.5" : "gap-1"}`}>
        <WorkspaceSwitcherButton />
        <IconTooltip label="Shared bots">
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label="Shared bots"
            className="app-no-drag h-7 w-7 rounded-full text-muted-foreground"
            onClick={() => navigate("/app/shared-bots")}
          >
            <Users size={17} />
          </Button>
        </IconTooltip>
        <IconTooltip label={t`Home`}>
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label={t`Home`}
            className="app-no-drag h-7 w-7 rounded-full text-muted-foreground"
            onClick={() => navigate("/app")}
          >
            <Home size={17} />
          </Button>
        </IconTooltip>
        {canExpand ? (
          <IconTooltip label={toggleLabel}>
            <Button
              variant="ghost"
              size="icon-sm"
              className="app-no-drag inline-flex h-7 w-7 items-center justify-center rounded-full text-muted-foreground/70 hover:text-foreground/75"
              aria-label={toggleLabel}
              data-testid={mini ? "restore-bots-sidebar" : "minimize-bots-sidebar"}
              onClick={() => setBotsSidebarCollapsedPref(!mini)}
            >
              {mini ? (
                <PanelLeftOpen size={15} strokeWidth={1.8} aria-hidden="true" />
              ) : (
                <PanelLeftClose size={15} strokeWidth={1.8} aria-hidden="true" />
              )}
            </Button>
          </IconTooltip>
        ) : null}
        <Popover open={createMenuOpen} onOpenChange={setCreateMenuOpen}>
          <IconTooltip label={t`Create`}>
            <PopoverTrigger
              className="app-no-drag flex size-7 items-center justify-center rounded-full text-[21px] text-muted-foreground/70 outline-none hover:text-foreground/75 focus-visible:ring-2 focus-visible:ring-ring"
              aria-label={t`Create`}
              data-testid="create-menu-trigger"
            >
              +
            </PopoverTrigger>
          </IconTooltip>
          {/* Unmount with the state change so the panel it opens never coexists with the menu. */}
          {createMenuOpen ? (
            <PopoverContent
              align="end"
              className="app-no-drag w-auto gap-0 overflow-hidden p-0 data-closed:animate-none"
            >
              <BotCreatePicker
                bots={bots}
                onCreateBot={() => {
                  setCreateMenuOpen(false);
                  setMobileSidebarOpen(false);
                  setPanel("create");
                }}
                onOpenBot={(id) => {
                  setCreateMenuOpen(false);
                  setMobileSidebarOpen(false);
                  navigate(`/app/${id}`);
                }}
                onCreateGroup={() => {
                  setCreateMenuOpen(false);
                  setMobileSidebarOpen(false);
                  setPanel("create-group");
                }}
                onCreateSharedBot={() => {
                  setCreateMenuOpen(false);
                  setMobileSidebarOpen(false);
                  navigate("/app/shared-bots?new=1");
                }}
                onCreateSpace={() => {
                  setCreateMenuOpen(false);
                  setMobileSidebarOpen(false);
                  setNewSpaceOpen(true);
                }}
                onShowGroupInfo={() => {
                  setCreateMenuOpen(false);
                  setMobileSidebarOpen(false);
                  setPickerInfoTopic("group");
                }}
                onShowSpaceInfo={() => {
                  setCreateMenuOpen(false);
                  setMobileSidebarOpen(false);
                  setPickerInfoTopic("space");
                }}
              />
            </PopoverContent>
          ) : null}
        </Popover>
      </div>
    </div>
  );
}
